// Ortak personel düzenleme formu (#29) — `usePersonnelEditor` kurar, `PersonnelSheet` düzenleme kipinde
// tek çocuk olarak gösterir. Sıra: Ad/Soyad · T.C. · Telefon/E-posta · Erişim seviyesi · Aktif.
//
// Kilitli alanlar `VehicleFormSheet`'in kilitli plaka anatomisidir (etiket satırı · `Lock` + değer ·
// gerekçe), hepsi statik metin — gri, input'a benzeyen kutu DEĞİL (sand/graphite/Carbon'da devre dışı
// alan gibi okunur). Kilitli T.C. RHF alanı değildir ve PUT'a girmez; sunucu-maskeli değer düz metindir
// (Göster/Gizle yok, `TcKimlikValue`'ya verilmez). Telefon/e-posta asla `mailto:`/`tel:` değildir.
//
// KURAL (Sheet notu): bu gövdeye `Tooltip`, `RowActionsMenu`, `SelectMenu` konmaz — `--z-dropdown`
// çekmecenin altında kalır. Yerel `Select` serbesttir.

import { Lock } from 'lucide-react';
import { type BaseSyntheticEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { Alert } from '../ui/Alert';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { PersonnelMaskNotice } from './PersonnelMaskNotice';
import { TcKimlikValue } from './TcKimlikValue';
import { type PersonnelEditTarget, isNameRequired, tcLock } from './personnelEdit';
import type { PersonnelFormValues } from './usePersonnelEditor';

export interface PersonnelEditBodyProps {
  form: UseFormReturn<PersonnelFormValues>;
  target: PersonnelEditTarget;
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  /** Alana bağlanamayan son kaydetme hatası; yoksa `null`. */
  generalError: Error | null;
}

const FIRST_ID = 'personnel-edit-firstName';
const LAST_ID = 'personnel-edit-lastName';
const TC_ID = 'personnel-edit-tc';
const ACCESS_ID = 'personnel-edit-access';
const ACTIVE_ID = 'personnel-edit-active';

/** `VehicleFormSheet` kilitli plaka anatomisi: etiket · `Lock` + değer · (varsa) gerekçe. */
function LockedField({
  label,
  labelId,
  reason,
  children,
}: {
  label: string;
  labelId?: string;
  reason?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span id={labelId} className="text-xs text-muted">
        {label}
      </span>
      <span className="inline-flex items-start gap-1.5 text-sm font-medium text-fg">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">{children}</span>
      </span>
      {reason ? <p className="text-xs text-muted">{reason}</p> : null}
    </div>
  );
}

export function PersonnelEditBody({
  form,
  target,
  onSubmit,
  generalError,
}: PersonnelEditBodyProps) {
  const { t } = useTranslation();
  const { register, setFocus, setValue, watch, formState } = form;
  const [tcRevealed, setTcRevealed] = useState(false);
  const tcLabelId = useId();
  const bannerRef = useRef<HTMLDivElement>(null);
  const lock = tcLock(target);
  const isActive = watch('isActive');
  const firstNameError = formState.errors.firstName?.message;
  const lastNameError = formState.errors.lastName?.message;
  const tcError = formState.errors.tcKimlikNo?.message;

  // Sheet yalnız AÇILIŞTA ilk alana odaklanır; çekmece içinde kip değişince odak buraya bizden gelir.
  useEffect(() => {
    setFocus('firstName');
  }, [setFocus]);

  // Formun dibinden basılan Kaydet'in genel hatası görünür olsun (1280×720'de form kayar).
  useEffect(() => {
    if (generalError) {
      bannerRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [generalError]);

  return (
    <form id="personnel-edit-form" className="flex flex-col gap-4" noValidate onSubmit={onSubmit}>
      {generalError ? (
        <div ref={bannerRef}>
          <Alert tone="danger">{resolveApiErrorMessage(generalError, t)}</Alert>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          htmlFor={FIRST_ID}
          label={t('personnelForm.field.firstName')}
          error={firstNameError}
          required={isNameRequired(target.firstName)}
        >
          <Input
            id={FIRST_ID}
            autoComplete="off"
            invalid={Boolean(firstNameError)}
            aria-describedby={firstNameError ? `${FIRST_ID}-error` : undefined}
            {...register('firstName')}
          />
        </Field>
        <Field
          htmlFor={LAST_ID}
          label={t('personnelForm.field.lastName')}
          error={lastNameError}
          required={isNameRequired(target.lastName)}
        >
          <Input
            id={LAST_ID}
            autoComplete="off"
            invalid={Boolean(lastNameError)}
            aria-describedby={lastNameError ? `${LAST_ID}-error` : undefined}
            {...register('lastName')}
          />
        </Field>
      </div>

      {lock === 'none' ? (
        <Field
          htmlFor={TC_ID}
          label={t('personnelForm.field.tcKimlikNo')}
          hint={t('personnelForm.field.tcKimlikNoHint')}
          error={tcError}
        >
          <Input
            id={TC_ID}
            inputMode="numeric"
            maxLength={11}
            autoComplete="off"
            invalid={Boolean(tcError)}
            aria-describedby={tcError ? `${TC_ID}-error` : `${TC_ID}-hint`}
            {...register('tcKimlikNo')}
          />
        </Field>
      ) : (
        <LockedField
          label={t('personnelForm.field.tcKimlikNo')}
          labelId={tcLabelId}
          reason={
            lock === 'setOnce'
              ? t('personnelForm.lock.tcSetOnce')
              : t('personnelForm.lock.tcNoPermission')
          }
        >
          {target.tcKimlikNo === null ? (
            '—'
          ) : lock === 'setOnce' ? (
            <TcKimlikValue
              tcKimlikNo={target.tcKimlikNo}
              revealed={tcRevealed}
              onToggle={() => setTcRevealed((r) => !r)}
              describedBy={tcLabelId}
            />
          ) : (
            <span className="tabular-nums">{target.tcKimlikNo}</span>
          )}
        </LockedField>
      )}

      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LockedField label={t('personnelForm.field.phone')}>{target.phone ?? '—'}</LockedField>
          <LockedField label={t('personnelForm.field.email')}>{target.email ?? '—'}</LockedField>
        </div>
        <p className="text-xs text-muted">{t('personnelForm.lock.contact')}</p>
        {target.personalDataMasked ? <PersonnelMaskNotice /> : null}
      </div>

      <Field
        htmlFor={ACCESS_ID}
        label={t('personnelForm.field.accessLevel')}
        hint={t('personnelForm.field.accessHint')}
      >
        <Select
          id={ACCESS_ID}
          aria-describedby={`${ACCESS_ID}-hint`}
          options={[
            { value: 'Personeli', label: t('personnelForm.access.personeli') },
            { value: 'Yetkili', label: t('personnelForm.access.yetkili') },
          ]}
          {...register('accessLevel')}
        />
      </Field>

      <Switch
        id={ACTIVE_ID}
        checked={isActive}
        onChange={(next) => setValue('isActive', next, { shouldDirty: true })}
        label={t('personnelForm.field.active')}
        description={
          <>
            <span className="block">{t('personnelForm.field.activeHint')}</span>
            <span className="mt-1 block">{t('personnelForm.refreshNote')}</span>
          </>
        }
      />
    </form>
  );
}
